import { useState, useCallback, useEffect, useRef, type FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Alert from '@/components/ui/Alert';
import { MfaCodeInput } from '@/components/auth';
import authApi from '@/services/auth';
import { passkeyApi } from '@/services/passkey';
import { ApiClientError, setAccessToken } from '@/services/api';
import { getDeviceFingerprint } from '@/utils/fingerprint';
import { isWebAuthnSupported, WebAuthnError } from '@/utils/webauthn';

interface LocationState {
  mfaToken: string;
  from?: { pathname: string };
  // Optional: if provided, indicates SMS is available for this user
  smsAvailable?: boolean;
  phoneLastFour?: string;
  // Device fingerprint passed from login page
  deviceFingerprint?: string;
  // Optional: if provided, indicates passkey is available for this user
  passkeyAvailable?: boolean;
}

type MfaMode = 'totp' | 'sms' | 'recovery' | 'passkey';

/**
 * MFA Challenge Page
 * Handles two-factor authentication code entry
 * Users arrive here after successful password authentication when MFA is enabled
 * Supports TOTP, SMS, and recovery code methods
 */
export default function MfaChallengePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyMfa, isLoading, error, clearError, setUser, clearMfaState } =
    useAuthStore();

  const state = location.state as LocationState | null;
  const mfaToken = state?.mfaToken;
  const from = state?.from?.pathname || '/dashboard';

  const [mode, setMode] = useState<MfaMode>('totp');
  const [code, setCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [localError, setLocalError] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);

  // SMS-specific state
  const [smsAvailable, setSmsAvailable] = useState<boolean | null>(state?.smsAvailable ?? null);
  const [phoneLastFour, setPhoneLastFour] = useState<string | null>(state?.phoneLastFour ?? null);
  const [smsSent, setSmsSent] = useState(false);
  const [smsSending, setSmsSending] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  // Passkey-specific state
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const webAuthnSupported = isWebAuthnSupported();

  // Focus target for a completed code. See handleCodeComplete.
  const verifyButtonRef = useRef<HTMLButtonElement | null>(null);

  // Device fingerprint - use from location state or fetch fresh
  // Track both the fingerprint value and whether it's ready (for device trust feature)
  const deviceFingerprintRef = useRef<string | null>(state?.deviceFingerprint ?? null);
  const [fingerprintReady, setFingerprintReady] = useState(!!state?.deviceFingerprint);

  // Initialize device fingerprint if not passed from login
  // This is important for MFA device trust - fingerprint MUST be available
  // when "trust this device" is checked, otherwise trust cannot be established
  useEffect(() => {
    const initFingerprint = async () => {
      if (!deviceFingerprintRef.current) {
        try {
          deviceFingerprintRef.current = await getDeviceFingerprint();
          setFingerprintReady(true);
        } catch {
          // Fingerprint collection failed - still mark as ready to not block MFA
          // Device trust just won't work in this case
          setFingerprintReady(true);
        }
      }
    };
    initFingerprint();
  }, []);

  // Clear countdown on unmount
  useEffect(() => {
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, []);

  // Start resend countdown
  const startResendCountdown = useCallback((seconds: number = 60) => {
    setResendCountdown(seconds);
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    countdownRef.current = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          if (countdownRef.current) {
            clearInterval(countdownRef.current);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  /**
   * Move focus to Verify when the code is complete. Do NOT submit.
   *
   * This used to call verifyMfa() directly, which made the form submit itself
   * the instant the 6th digit landed - before the user could reach the "Trust
   * this device for 30 days" checkbox rendered below the autofocused input. The
   * request went out with trustDevice=false every time, and on success the page
   * navigated away, so there was no second chance. That is the whole reason
   * "remember me for 30 days" appeared not to work, and it showed up on TOTP
   * first because TOTP has no pause: it is the default mode, the input is
   * autofocused, and the code is already sitting in the authenticator app.
   *
   * A form that submits itself when one field completes cannot host any other
   * user-settable option - the option is unreachable by construction. So the
   * auto-submit is gone rather than tuned.
   *
   * Focusing Verify keeps the convenience that mattered: Enter submits. And the
   * cost is paid on a screen that, once device trust works, a user sees at most
   * once per 30 days.
   */
  const handleCodeComplete = useCallback(() => {
    verifyButtonRef.current?.focus();
  }, []);

  /**
   * Leave the challenge and return to the sign-in form.
   *
   * The store must be cleared FIRST. `mfaRequired`/`mfaToken` are still set from
   * the login that sent the user here, and LoginPage has an effect that
   * redirects to /mfa whenever both are present (LoginPage.tsx, WP-AUTH-7, added
   * so an OAuth login that needs MFA lands on this page). So navigating without
   * clearing bounced the user straight back here: "Back to sign in" did nothing
   * visible, and there was no way off the challenge screen short of reloading.
   *
   * `clearMfaState` has existed in the store for exactly this, and nothing had
   * ever called it.
   *
   * The "Session Expired" branch above uses a plain <Link> and needs no such
   * clearing: it renders only when the router carries no mfaToken, which happens
   * after a reload - and `partialize` (authStore.ts) keeps mfaRequired/mfaToken
   * out of persisted state, so a reload has already emptied them.
   */
  const handleBackToSignIn = useCallback(() => {
    clearMfaState();
    clearError();
    navigate('/login', { replace: true });
  }, [clearMfaState, clearError, navigate]);

  // If no MFA token in state, redirect to login
  if (!mfaToken) {
    return (
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
          <svg
            className="h-6 w-6 text-yellow-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Session Expired</h2>
        <p className="text-gray-600 mb-6">
          Your authentication session has expired. Please sign in again.
        </p>
        <Link to="/login">
          <Button className="w-full">Back to sign in</Button>
        </Link>
      </div>
    );
  }

  // Send SMS code
  const sendSmsCode = async () => {
    if (!mfaToken) return;

    setSmsSending(true);
    setLocalError('');
    clearError();

    try {
      const response = await authApi.sendSmsForChallenge(mfaToken);
      setPhoneLastFour(response.phoneLastFour);
      setSmsAvailable(true);
      setSmsSent(true);
      startResendCountdown();
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 404 || err.code === 'SMS_NOT_ENABLED') {
          // SMS not available for this user
          setSmsAvailable(false);
          setLocalError('SMS authentication is not enabled for your account.');
        } else if (err.status === 429) {
          const retryAfter = err.retryAfter || 60;
          setLocalError(`Please wait ${retryAfter} seconds before requesting another code`);
          startResendCountdown(retryAfter);
        } else if (err.status === 503) {
          setLocalError('SMS service is temporarily unavailable. Please try again later.');
        } else {
          setLocalError(err.detail);
        }
      } else {
        setLocalError('Failed to send SMS code. Please try again.');
      }
    } finally {
      setSmsSending(false);
    }
  };

  // Handle resend SMS
  const handleResendSms = async () => {
    if (resendCountdown > 0) return;
    await sendSmsCode();
  };

  // Handle passkey authentication
  const handlePasskeyAuth = async () => {
    if (!mfaToken || !webAuthnSupported) return;

    setPasskeyLoading(true);
    setLocalError('');
    clearError();

    try {
      const response = await passkeyApi.authenticateWithPasskeyMfa(mfaToken);

      // WP-AUTH-12: Only set access token - refresh token is managed via httpOnly cookie
      if (response.tokens) {
        setAccessToken(response.tokens.accessToken);
      }
      if (response.user) {
        setUser(response.user);
      }

      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof WebAuthnError) {
        if (err.type === 'cancelled') {
          // User cancelled, don't show error
          setLocalError('');
        } else {
          setLocalError(err.message);
        }
      } else if (err instanceof ApiClientError) {
        setLocalError(err.detail);
      } else if (err instanceof Error) {
        setLocalError(err.message);
      } else {
        setLocalError('Failed to authenticate with passkey. Please try another method.');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Passkey mode uses its own handler, not the form submit
    if (mode === 'passkey') {
      await handlePasskeyAuth();
      return;
    }

    clearError();
    setLocalError('');

    let codeToSubmit: string;
    let methodToUse: 'totp' | 'sms' | 'recovery';

    if (mode === 'recovery') {
      codeToSubmit = recoveryCode.toUpperCase().trim();
      methodToUse = 'recovery';

      if (codeToSubmit.length !== 8) {
        setLocalError('Recovery code must be 8 characters');
        return;
      }
      if (!/^[A-Za-z0-9]{8}$/.test(codeToSubmit)) {
        setLocalError('Recovery code must be 8 alphanumeric characters');
        return;
      }
    } else {
      codeToSubmit = code;
      methodToUse = mode as 'totp' | 'sms';

      if (codeToSubmit.length !== 6) {
        setLocalError('Please enter a 6-digit code');
        return;
      }
    }

    try {
      // Pass device fingerprint and trust flag with MFA verification
      await verifyMfa(
        mfaToken,
        codeToSubmit,
        methodToUse,
        deviceFingerprintRef.current || undefined,
        trustDevice
      );
      navigate(from, { replace: true });
    } catch {
      // Error is handled by the store
      if (mode !== 'recovery') {
        setCode(''); // Clear code on error for retry
      }
    }
  };

  const switchMode = (newMode: MfaMode) => {
    setMode(newMode);
    setCode('');
    setRecoveryCode('');
    setLocalError('');
    clearError();

    // When switching to SMS, send the code
    if (newMode === 'sms' && !smsSent) {
      sendSmsCode();
    }

    // When switching to passkey, start the authentication
    if (newMode === 'passkey') {
      handlePasskeyAuth();
    }
  };

  const displayError = localError || error;

  // Determine what description to show
  const getModeDescription = () => {
    switch (mode) {
      case 'totp':
        return 'Enter the 6-digit code from your authenticator app to continue.';
      case 'sms':
        return phoneLastFour
          ? `Enter the 6-digit code sent to ***-***-${phoneLastFour}.`
          : 'Enter the 6-digit code sent to your phone.';
      case 'recovery':
        return 'Enter one of your 8-character recovery codes.';
      case 'passkey':
        return 'Use your passkey to authenticate.';
    }
  };

  return (
    <div>
      <div className="text-center mb-6">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 mb-4">
          <svg
            className="h-6 w-6 text-primary-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Two-Factor Authentication
        </h2>
        <p className="text-sm text-gray-600">
          {getModeDescription()}
        </p>
      </div>

      {displayError && (
        <Alert
          variant="error"
          className="mb-4"
          onDismiss={() => {
            clearError();
            setLocalError('');
          }}
        >
          {displayError}
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {mode === 'passkey' ? (
          <div className="flex justify-center py-8">
            <div className="text-center">
              {passkeyLoading ? (
                <>
                  <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-primary-100 mb-4">
                    <svg
                      className="h-8 w-8 text-primary-600 animate-pulse"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4"
                      />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-600">Follow your browser prompts to authenticate...</p>
                </>
              ) : (
                <>
                  <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gray-100 mb-4">
                    <svg
                      className="h-8 w-8 text-gray-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4"
                      />
                    </svg>
                  </div>
                  <Button
                    type="button"
                    onClick={handlePasskeyAuth}
                    disabled={passkeyLoading}
                  >
                    Try again
                  </Button>
                </>
              )}
            </div>
          </div>
        ) : mode === 'recovery' ? (
          <Input
            label="Recovery Code"
            type="text"
            value={recoveryCode}
            onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
            placeholder="XXXXXXXX"
            maxLength={8}
            autoComplete="off"
            autoFocus
            disabled={isLoading}
            className="text-center font-mono text-lg tracking-widest uppercase"
          />
        ) : mode === 'sms' && smsSending ? (
          <div className="flex justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Sending code...</p>
            </div>
          </div>
        ) : (
          <div className="flex justify-center">
            <MfaCodeInput
              value={code}
              onChange={setCode}
              onComplete={handleCodeComplete}
              disabled={isLoading || smsSending}
              error={displayError ? ' ' : undefined}
              autoFocus
            />
          </div>
        )}

        {/* Resend button for SMS mode */}
        {mode === 'sms' && smsSent && !smsSending && (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={handleResendSms}
              disabled={resendCountdown > 0 || isLoading}
              className="text-sm text-primary-600 hover:text-primary-500 disabled:text-gray-400"
            >
              {resendCountdown > 0
                ? `Resend code in ${resendCountdown}s`
                : 'Resend code'}
            </button>
          </div>
        )}

        {/* Trust device checkbox */}
        {/* Disabled until fingerprint is ready to ensure device trust can be established */}
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="trust-device"
            checked={trustDevice}
            onChange={(e) => setTrustDevice(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
            disabled={isLoading || smsSending || !fingerprintReady}
          />
          <label htmlFor="trust-device" className={fingerprintReady ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}>
            <span className="text-sm font-medium text-gray-900">
              Trust this device for 30 days
            </span>
            <p className="text-xs text-gray-500 mt-0.5">
              {fingerprintReady
                ? "You won't need to enter a code on this device."
                : 'Preparing device trust...'}
            </p>
          </label>
        </div>

        <Button
          ref={verifyButtonRef}
          type="submit"
          className="w-full"
          isLoading={isLoading}
          disabled={smsSending}
        >
          Verify
        </Button>
      </form>

      <div className="mt-6 space-y-4">
        <div className="text-center">
          <p className="text-sm text-gray-500">
            {mode === 'totp' &&
              'Open your authenticator app (like Google Authenticator or Authy) and enter the code shown for FlowIO.'}
            {mode === 'sms' &&
              'A verification code was sent to your registered phone number.'}
            {mode === 'recovery' &&
              'Recovery codes were provided when you first set up MFA. Each code can only be used once.'}
            {mode === 'passkey' &&
              'Use Face ID, Touch ID, Windows Hello, or a security key to verify your identity.'}
          </p>
        </div>

        <div className="border-t border-gray-200 pt-4 space-y-2">
          {/* Method switcher options */}
          {mode === 'totp' && (
            <div className="text-center space-y-2">
              {/* Show passkey option if WebAuthn is supported */}
              {webAuthnSupported && (
                <p className="text-sm text-gray-600">
                  <button
                    type="button"
                    className="font-medium text-primary-600 hover:text-primary-500"
                    onClick={() => switchMode('passkey')}
                    disabled={isLoading || smsSending || passkeyLoading}
                  >
                    Use passkey instead
                  </button>
                </p>
              )}
              {/* Only show SMS option if not explicitly known to be unavailable */}
              {smsAvailable !== false && (
                <p className="text-sm text-gray-600">
                  <button
                    type="button"
                    className="font-medium text-primary-600 hover:text-primary-500"
                    onClick={() => switchMode('sms')}
                    disabled={isLoading || smsSending}
                  >
                    Send code via SMS
                  </button>
                  {phoneLastFour && ` to ***-***-${phoneLastFour}`}
                </p>
              )}
              <p className="text-sm text-gray-600">
                Having trouble?{' '}
                <button
                  type="button"
                  className="font-medium text-primary-600 hover:text-primary-500"
                  onClick={() => switchMode('recovery')}
                  disabled={isLoading}
                >
                  Use a recovery code
                </button>
              </p>
            </div>
          )}

          {mode === 'sms' && (
            <div className="text-center space-y-2">
              {webAuthnSupported && (
                <p className="text-sm text-gray-600">
                  <button
                    type="button"
                    className="font-medium text-primary-600 hover:text-primary-500"
                    onClick={() => switchMode('passkey')}
                    disabled={isLoading || smsSending || passkeyLoading}
                  >
                    Use passkey instead
                  </button>
                </p>
              )}
              <p className="text-sm text-gray-600">
                Have your authenticator?{' '}
                <button
                  type="button"
                  className="font-medium text-primary-600 hover:text-primary-500"
                  onClick={() => switchMode('totp')}
                  disabled={isLoading || smsSending}
                >
                  Use authenticator code
                </button>
              </p>
              <p className="text-sm text-gray-600">
                <button
                  type="button"
                  className="font-medium text-primary-600 hover:text-primary-500"
                  onClick={() => switchMode('recovery')}
                  disabled={isLoading || smsSending}
                >
                  Use a recovery code
                </button>
              </p>
            </div>
          )}

          {mode === 'recovery' && (
            <div className="text-center space-y-2">
              {webAuthnSupported && (
                <p className="text-sm text-gray-600">
                  <button
                    type="button"
                    className="font-medium text-primary-600 hover:text-primary-500"
                    onClick={() => switchMode('passkey')}
                    disabled={isLoading || passkeyLoading}
                  >
                    Use passkey instead
                  </button>
                </p>
              )}
              <p className="text-sm text-gray-600">
                Have your authenticator?{' '}
                <button
                  type="button"
                  className="font-medium text-primary-600 hover:text-primary-500"
                  onClick={() => switchMode('totp')}
                  disabled={isLoading}
                >
                  Use authenticator code
                </button>
              </p>
              {smsAvailable !== false && (
                <p className="text-sm text-gray-600">
                  <button
                    type="button"
                    className="font-medium text-primary-600 hover:text-primary-500"
                    onClick={() => switchMode('sms')}
                    disabled={isLoading || smsSending}
                  >
                    Send code via SMS
                  </button>
                </p>
              )}
            </div>
          )}

          {mode === 'passkey' && (
            <div className="text-center space-y-2">
              <p className="text-sm text-gray-600">
                Have your authenticator?{' '}
                <button
                  type="button"
                  className="font-medium text-primary-600 hover:text-primary-500"
                  onClick={() => switchMode('totp')}
                  disabled={passkeyLoading}
                >
                  Use authenticator code
                </button>
              </p>
              {smsAvailable !== false && (
                <p className="text-sm text-gray-600">
                  <button
                    type="button"
                    className="font-medium text-primary-600 hover:text-primary-500"
                    onClick={() => switchMode('sms')}
                    disabled={passkeyLoading || smsSending}
                  >
                    Send code via SMS
                  </button>
                </p>
              )}
              <p className="text-sm text-gray-600">
                <button
                  type="button"
                  className="font-medium text-primary-600 hover:text-primary-500"
                  onClick={() => switchMode('recovery')}
                  disabled={passkeyLoading}
                >
                  Use a recovery code
                </button>
              </p>
            </div>
          )}
        </div>

        <button
          type="button"
          className="w-full text-sm text-gray-600 hover:text-gray-900"
          onClick={handleBackToSignIn}
          disabled={isLoading || smsSending}
        >
          Back to sign in
        </button>
      </div>
    </div>
  );
}
